import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from 'react-router-dom';
import Profile from '../views/Profile';
import ExternalApi from '../views/ExternalApi';
import ProductList from '../views/ProductList';
import ErrorPage from '../views/Errorpage';
import Layout from '../components/Layout';
import Logout from '../components/Logout';
import { useAuth } from 'react-oidc-context';
import Orders from '../views/Orders';
import { ROLE } from '../interface/interface';
import { useContext, useEffect } from 'react';
import Cart from '../views/Cart';
import ProductDetails from '../views/ProductDetails';
import OnboardPatient from '../views/OnboardPatient';
import ResetPassword from '../views/ResetPassword';
import ListErxToken from '../views/ListErxToken';
import RenderHome from '../components/RenderHome';
import { UserContext } from '../context/userContext';
import Loading from '../components/Loading';
import { AdminOrderTabs } from '../views/AdminOrderTabs';
import { publicUrls } from '../constant/constant';
import PatientEdit from '../views/PatientEdit';
import OrderSummaryAdminView from '../components/OrderSummaryAdminView';
import OrderSummaryPatientView from '../components/OrderSummaryPatientView';
import OrderHistory from '../components/OrderHistory';
import PatientTabs from '../views/PatientTabs';
import ListClinics from '../views/ListClinics';
import Clinic from '../views/Clinic';
import { DoctorCU } from '../views/DoctorCU';
import RegisterPatient from '../views/RegiserPatient';
import { DoctorTab } from '../views/Doctors';
import OnboardUser from '../views/OnboardUser';
import ResetPasswordMail from '../views/ResetPasswordMail';
import ResetPasswordWithConsent from '../views/ResetPasswordWithConsent';
import { TrackingHistory } from '../components/TrackingHistory';
import Patients from '../views/Patient';
import DoctorPatientTabs from '../views/DoctorPatientTabs';
import ProductTypeTabs from '../views/ProductTypetabs';
import NonScriptRange from '../views/NonScriptRange';
import MonthlyLimitTabs from '../components/MonthlyLimitTabs';
import { PatientMedicationTabs } from '../views/PatientMedicationTabs';
import GuestNonScriptRange from '../views/guest/GuestNonScriptRange';
import GuestCart from '../views/guest/GuestCart';
import { PatientDosageTabs } from '../views/PatientDosageTabs';
import ContactUsForm from '../components/contactUsForm';
import { nativeService } from '../helpers/nativeService';
import { NotificationInbox } from '../views/NotificationInbox';

const Routers = () => {
  const { user, isAuthenticated } = useAuth();

  const isPublicUrl = () => {
    return publicUrls.some((publicUrl) =>
      window.location.pathname.startsWith(publicUrl),
    );
  };

  if (!isPublicUrl() && (!user || !isAuthenticated)) {
    return <Logout />;
  }

  useEffect(() => {
    if (!user) return;
    const nativeWebView = (window as any)?.ReactNativeWebView;
    const hasWebView = !!nativeWebView;
    if (hasWebView) {
      nativeService(
        JSON.stringify({
          token: user?.access_token,
        }),
      );
    }
  }, [!!(window as any)?.ReactNativeWebView, user]);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Layout />}>
          {user ? (
            <>
              <Route path="/" element={<RenderHome />} />
              <Route path="/home" element={<RenderHome />} />
              <Route path="/contact-us" element={<ContactUsForm />} />
              <Route
                path="/profile"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <Profile />
                  </PrivateRoute>
                }
              />
              <Route path="/external-api" element={<ExternalApi />} />
              <Route
                path="/admin/orders"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.Dispense_Tech,
                      ROLE.PLO,
                      ROLE.Pharmacist,
                    ]}
                  >
                    <AdminOrderTabs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/order/history/:orderId"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.PLO,
                      ROLE.Dispense_Tech,
                      ROLE.Pharmacist,
                    ]}
                  >
                    <OrderHistory />
                  </PrivateRoute>
                }
              />
              <Route
                path="/order/tracking/:orderId"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.PLO,
                      ROLE.Dispense_Tech,
                      ROLE.Pharmacist,
                      ROLE.Patients,
                    ]}
                  >
                    <TrackingHistory />
                  </PrivateRoute>
                }
              />
              <Route
                path="/storefront"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <ProductList home={false} />
                  </PrivateRoute>
                }
              />
              <Route
                path="/non-script-range"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <NonScriptRange />
                  </PrivateRoute>
                }
              />
              <Route
                path="/cart"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <Cart />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.PLO,
                      ROLE.Dispense_Tech,
                      ROLE.Pharmacist,
                    ]}
                  >
                    <PatientTabs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patient/list"
                element={
                  <PrivateRoute roles={[ROLE.Doctors]}>
                    <Patients />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patient/:patientId"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.PLO,
                      ROLE.Dispense_Tech,
                      ROLE.Pharmacist,
                    ]}
                  >
                    <PatientEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patient/:patientId/orders"
                element={
                  <PrivateRoute roles={[ROLE.Doctors, ROLE.Pharmacist]}>
                    <DoctorPatientTabs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/prescriptions"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <PatientMedicationTabs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dosage-diary"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <PatientDosageTabs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/monthly-limit"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <MonthlyLimitTabs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/orders"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <Orders />
                  </PrivateRoute>
                }
              />
              <Route
                path="/product/:productId"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Patients,
                      ROLE.Admin,
                      ROLE.Doctors,
                      ROLE.PLO,
                      ROLE.Dispense_Tech,
                      ROLE.Pharmacist,
                    ]}
                  >
                    <ProductDetails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/new-patient"
                element={
                  <PrivateRoute roles={[ROLE.Admin, ROLE.PLO]}>
                    <OnboardPatient />
                  </PrivateRoute>
                }
              />
              <Route
                path="/internal-user"
                element={
                  <PrivateRoute roles={[ROLE.Admin]}>
                    <OnboardUser />
                  </PrivateRoute>
                }
              />
              <Route
                path="/doctors"
                element={
                  <PrivateRoute roles={[ROLE.Admin]}>
                    <DoctorTab />
                  </PrivateRoute>
                }
              />
              <Route
                path="/clinics"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.PLO,
                      ROLE.Dispense_Tech,
                      ROLE.Pharmacist,
                    ]}
                  >
                    <ListClinics />
                  </PrivateRoute>
                }
              />
              <Route
                path="/doctor/edit"
                element={
                  <PrivateRoute roles={[ROLE.Admin]}>
                    <DoctorCU mode="create" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/doctor/edit/:doctorId"
                element={
                  <PrivateRoute roles={[ROLE.Admin]}>
                    <DoctorCU mode="update" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/clinic/:clinicId?"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.PLO,
                      ROLE.Dispense_Tech,
                      ROLE.Pharmacist,
                    ]}
                  >
                    <Clinic />
                  </PrivateRoute>
                }
              />
              <Route
                path="/tokens"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.PLO,
                      ROLE.Pharmacist,
                      ROLE.Dispense_Tech,
                    ]}
                  >
                    <ListErxToken />
                  </PrivateRoute>
                }
              />
              <Route
                path="/order-summary-patient/:orderId"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <OrderSummaryPatientView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/order-summary/:orderId"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.Dispense_Tech,
                      ROLE.PLO,
                      ROLE.Pharmacist,
                    ]}
                  >
                    <OrderSummaryAdminView />
                  </PrivateRoute>
                }
              />

              <Route
                path="/products"
                element={
                  <PrivateRoute
                    roles={[
                      ROLE.Admin,
                      ROLE.PLO,
                      ROLE.Dispense_Tech,
                      ROLE.Pharmacist,
                    ]}
                  >
                    <ProductTypeTabs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/contact-us"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <ContactUsForm />
                  </PrivateRoute>
                }
              />
              {/* For custom redirection */}
              <Route path="/404" element={<ErrorPage />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="*" element={<ErrorPage />} />
              <Route
                path="/reset-password-mail"
                element={<ResetPasswordMail />}
              />
              <Route path="/reset-password/:id" element={<ResetPassword />} />
              <Route
                path="/notification-inbox"
                element={
                  <PrivateRoute roles={[ROLE.Patients]}>
                    <NotificationInbox />
                  </PrivateRoute>
                }
              />
            </>
          ) : (
            <>
              <Route path="/register" element={<RegisterPatient />} />
              <Route
                path="/reset-password/consent/:id"
                element={<ResetPasswordWithConsent />}
              />
              <Route path="/reset-password/:id" element={<ResetPassword />} />
              <Route
                path="/reset-password-mail"
                element={<ResetPasswordMail />}
              />
              <Route
                path="/guest/shopping-products"
                element={<GuestNonScriptRange />}
              />
              <Route path="/guest/cart" element={<GuestCart />} />
              <Route
                path="/guest/product/:productId"
                element={<ProductDetails isGuest={true} />}
              />
            </>
          )}
        </Route>
      </Routes>
    </Router>
  );
};

export default Routers;

const PrivateRoute = ({
  children,
  roles,
}: {
  children: JSX.Element;
  roles: Array<ROLE>;
}) => {
  const location = useLocation();
  const { isAuthenticated, user, isLoading } = useAuth();
  const { userDetail } = useContext(UserContext);

  if (isLoading || !userDetail) {
    return <Loading />;
  }

  const userHasRequiredRole = user && roles.includes(userDetail.role as ROLE);

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} />;
  }

  if (!userHasRequiredRole) {
    return <ErrorPage />;
  }

  return children;
};
